import { monitorScopeLabel } from "./NoteMonitorCard";
import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Plus, Search, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button, Modal } from "../../components/ui";
import { deleteWorkflowDraft, getWorkflowDraft, listWorkflowDrafts, setReminderEnabled, WorkflowBuilderApiError } from "./api";
import type { DraftSummary, WorkflowDraft } from "./models";
import { useBuilderIdentity } from "./useBuilderIdentity";
import { formatReminderNextRun, formatReminderSchedule, ReminderStatus } from "./ReminderTaskCard";
import { useReminderStrings } from "./reminderStrings";
import browserStyles from "../../components/NoteWorkflows/NoteWorkflows.module.css";
import styles from "./WorkflowBuilder.module.css";
import reminderStyles from "./ReminderTask.module.css";
import type { WorkflowLibraryCard, WorkflowLibraryView } from "../../components/NoteWorkflows/workflowLibraryTypes";

type Props = {
    onOpen: (draftId: string) => void;
    onCreate: () => void;
    organizationId?: string;
    title?: string;
    renderLibrary?: (library: WorkflowLibraryView) => ReactNode;
};
export function WorkflowDraftLibrary(props: Props) {
    const { resolveToken, scope, switching } = useBuilderIdentity();
    // Scope changes discard confirmation state and abort requests from the previous identity.
    return <WorkflowDraftLibraryContent key={`${scope}:${switching}:${props.organizationId ?? "personal"}`} {...props} resolveToken={resolveToken} switching={switching} />;
}

type ContentProps = Props & Pick<ReturnType<typeof useBuilderIdentity>, "resolveToken" | "switching">;
type ReminderToggleOperation = { controller: AbortController; enabled: boolean; recovering: boolean };

function toDraftSummary(draft: WorkflowDraft): DraftSummary {
    return {
        id: draft.id, title: draft.workflow.title, description: draft.workflow.description,
        revision: draft.revision, updated_at: draft.updated_at, box_count: draft.workflow.boxes.length,
        kind: draft.workflow.kind, reminder: draft.workflow.reminder, workflow_revision: draft.workflow_revision,
        schedule_state: draft.schedule_state
    };
}

function WorkflowDraftLibraryContent({ onOpen, onCreate, resolveToken, switching, organizationId, title, renderLibrary }: ContentProps) {
    const { t, i18n } = useTranslation();
    const reminderWords = useReminderStrings();
    const [drafts, setDrafts] = useState<DraftSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [attempt, setAttempt] = useState(0);
    const [searchQuery, setSearchQuery] = useState("");
    const [deleteTarget, setDeleteTarget] = useState<DraftSummary | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState("");
    const [deleteConflict, setDeleteConflict] = useState(false);
    const [pendingToggles, setPendingToggles] = useState<ReadonlyMap<string, boolean>>(() => new Map());
    const [toggleErrors, setToggleErrors] = useState<ReadonlyMap<string, string>>(() => new Map());
    const toggling = useRef(new Map<string, ReminderToggleOperation>());
    // Opening another screen unmounts this library and aborts unfinished saves.
    const navigationBlocked = switching || deleting || pendingToggles.size > 0;
    const deletion = useRef<AbortController | null>(null);
    const deleteTrigger = useRef<HTMLButtonElement | null>(null);
    const list = useRef<HTMLElement | null>(null);
    const normalizedSearchQuery = searchQuery.trim().toLocaleLowerCase();
    const visibleDrafts = normalizedSearchQuery
        ? drafts.filter(draft => [draft.title, draft.description].some(value => value.toLocaleLowerCase().includes(normalizedSearchQuery)))
        : drafts;
    const workflowTitle = (draft: DraftSummary) => draft.title.trim() || t("settingsPage.company.noteWorkflows.unnamedWorkflow", {
        defaultValue: "Untitled workflow · {{id}}", id: draft.id
    });
    useEffect(() => () => {
        deletion.current?.abort();
        for (const operation of toggling.current.values()) operation.controller.abort();
        toggling.current.clear();
    }, []);
    useEffect(() => {
        const controller = new AbortController();
        setDrafts([]); setError(""); setLoading(true);
        if (!switching) void (async () => {
            try {
                const result = await listWorkflowDrafts(await resolveToken(), controller.signal, organizationId);
                if (!controller.signal.aborted) setDrafts(result);
            } catch (caught: unknown) {
                if (!controller.signal.aborted) setError(caught instanceof Error ? caught.message : t("workflowBuilder.loadError"));
            } finally { if (!controller.signal.aborted) setLoading(false); }
        })();
        return () => controller.abort();
    }, [organizationId, resolveToken, switching, attempt, t]);

    const closeDelete = () => {
        if (deletion.current) return;
        setDeleteTarget(null);
        setDeleteError("");
        setDeleteConflict(false);
    };
    const toggleReminder = async (target: DraftSummary) => {
        if (!target.schedule_state || switching || deleting) return;
        const pending = toggling.current.get(target.id);
        if (pending?.recovering) return;
        const enabled = !(pending?.enabled ?? target.schedule_state.enabled);
        // Keep the server snapshot intact so a failed change rolls back without touching other cards.
        setPendingToggles(current => new Map(current).set(target.id, enabled));
        setToggleErrors(current => { const next = new Map(current); next.delete(target.id); return next; });
        if (pending) {
            pending.enabled = enabled;
            return;
        }
        const controller = new AbortController();
        const operation: ReminderToggleOperation = { controller, enabled, recovering: false };
        toggling.current.set(target.id, operation);
        try {
            const token = await resolveToken();
            if (controller.signal.aborted) return;
            let confirmed = target;
            // Serialize writes using confirmed revisions, coalescing clicks to the latest desired state.
            while (confirmed.schedule_state && operation.enabled !== confirmed.schedule_state.enabled) {
                const result = await setReminderEnabled(target.id, {
                    request_id: crypto.randomUUID(), expected_revision: confirmed.revision, enabled: operation.enabled
                }, token, controller.signal, organizationId);
                if (controller.signal.aborted) return;
                const updated = toDraftSummary(result);
                confirmed = updated;
                setDrafts(current => current.map(item => item.id === updated.id ? updated : item));
            }
        } catch (caught: unknown) {
            if (controller.signal.aborted) return;
            operation.recovering = true;
            setToggleErrors(current => new Map(current).set(target.id, caught instanceof Error ? caught.message : reminderWords.toggleError));
            if (caught instanceof WorkflowBuilderApiError && caught.status === 409) {
                // Refresh only the conflicting workflow; other cards may be saving independently.
                try {
                    const token = await resolveToken();
                    if (controller.signal.aborted) return;
                    const latest = await getWorkflowDraft(target.id, token, controller.signal, organizationId);
                    if (!controller.signal.aborted) setDrafts(current => current.map(item => item.id === latest.id ? toDraftSummary(latest) : item));
                } catch (refreshError: unknown) {
                    if (!controller.signal.aborted) setToggleErrors(current => new Map(current).set(target.id, refreshError instanceof Error ? refreshError.message : reminderWords.toggleError));
                }
            }
        } finally {
            if (toggling.current.get(target.id) === operation) toggling.current.delete(target.id);
            if (!controller.signal.aborted) setPendingToggles(current => { const next = new Map(current); next.delete(target.id); return next; });
        }
    };
    const confirmDelete = async () => {
        if (!deleteTarget || switching || deletion.current) return;
        const target = deleteTarget;
        const controller = new AbortController();
        deletion.current = controller;
        setDeleting(true);
        setDeleteError("");
        setDeleteConflict(false);
        try {
            const token = await resolveToken();
            if (controller.signal.aborted) return;
            await deleteWorkflowDraft(target.id, target.revision, token, controller.signal, organizationId);
            if (controller.signal.aborted) return;
            setDrafts(current => current.filter(draft => draft.id !== target.id));
            setDeleteTarget(null);
        } catch (caught: unknown) {
            if (!controller.signal.aborted) {
                const conflict = caught instanceof WorkflowBuilderApiError && caught.status === 409;
                setDeleteConflict(conflict);
                setDeleteError(conflict ? t("workflowBuilder.deleteWorkflowConflict") : caught instanceof Error ? caught.message : t("workflowBuilder.deleteWorkflowError"));
            }
        } finally {
            if (deletion.current === controller) deletion.current = null;
            if (!controller.signal.aborted) setDeleting(false);
        }
    };
    const renderDraftCard = (draft: DraftSummary, blocked = false): ReactNode => {
        const busy = pendingToggles.has(draft.id);
        const pendingEnabled = pendingToggles.get(draft.id);
        const toggleError = toggleErrors.get(draft.id);
        const state = draft.schedule_state && pendingEnabled !== undefined && !toggleErrors.has(draft.id)
            ? { ...draft.schedule_state, enabled: pendingEnabled, completed: false, next_run_at: null }
            : draft.schedule_state;
        return <div key={draft.id} className={styles.libraryCard}>
            <button type="button" className={`${browserStyles.workflowNameCard} ${styles.libraryOpen}`}
                aria-label={workflowTitle(draft)} disabled={navigationBlocked || blocked}
                data-saving={pendingToggles.size > 0 && !switching && !deleting}
                onClick={() => onOpen(draft.id)}>
                <span className={browserStyles.workflowCardName}>{workflowTitle(draft)}</span>
                <span className={`${browserStyles.workflowCardSummary} ${styles.librarySummary}`}>{draft.kind === "reminder" && draft.reminder ? draft.reminder.project_summary?.instructions ?? draft.reminder.message : draft.description}</span>
                {draft.kind === "reminder" && draft.reminder ? <span className={reminderStyles.libraryMetadata}>
                    {draft.reminder.project_summary && <><span>{draft.reminder.project_summary.output_format === "docx" ? reminderWords.report : reminderWords.summary}</span><span>{reminderWords.project}: {draft.reminder.project_summary.project_name}</span></>}
                    {draft.reminder.note_monitor && <><span>{draft.reminder.note_monitor.project_name} · {draft.reminder.note_monitor.note_workflow_title} ≥ {draft.reminder.note_monitor.threshold}</span><span>{monitorScopeLabel(draft.reminder.note_monitor.scope, i18n.language)}</span>{draft.reminder.note_monitor.content_condition && <span className={reminderStyles.libraryCondition} title={draft.reminder.note_monitor.content_condition}>{i18n.language.startsWith("zh") ? "内容条件" : "Content condition"}: {draft.reminder.note_monitor.content_condition}</span>}</>}
                    {draft.reminder.schedule && <span>{formatReminderSchedule(draft.reminder.schedule, i18n.language)}</span>}
                    {draft.reminder.schedule && state?.next_run_at && state.workflow_revision === draft.workflow_revision && <small>{draft.reminder.project_summary?.output_format === "docx" ? reminderWords.reportNextRun : draft.reminder.project_summary ? reminderWords.summaryNextRun : reminderWords.nextRun}: {formatReminderNextRun(state.next_run_at, draft.reminder.schedule.timezone, i18n.language)}</small>}
                    <ReminderStatus state={state} />
                    {draft.schedule_state && draft.schedule_state.workflow_revision !== draft.workflow_revision && <small>{reminderWords.pending}</small>}
                </span> : <span className={reminderStyles.libraryMetadata}>
                    <span className={browserStyles.workflowCardCount}>{t("settingsPage.company.noteWorkflows.cardStepCount", { defaultValue: "{{count}} steps", count: draft.box_count })}</span>
                    {state && <><ReminderStatus state={state} /><small>{reminderWords.pending}</small></>}
                </span>}
            </button>
            <div className={reminderStyles.libraryActions}>
            {(draft.kind === "reminder" || draft.schedule_state) && <button type="button" role="switch" aria-checked={Boolean(state?.enabled)}
                aria-label={`${draft.reminder?.project_summary ? reminderWords.summaryToggle : reminderWords.toggle}: ${workflowTitle(draft)}`} aria-busy={busy}
                title={draft.schedule_state ? draft.reminder?.project_summary ? reminderWords.summaryToggle : reminderWords.toggle : reminderWords.notSaved}
                disabled={switching || deleting || (busy && toggleErrors.has(draft.id)) || !draft.schedule_state}
                onClick={() => { void toggleReminder(draft); }}><span /></button>}
            <Button variant="ghost" size="sm" iconOnly iconShape="square" disabled={blocked || switching || deleting || busy}
                aria-label={t("workflowBuilder.deleteWorkflowLabel", { title: workflowTitle(draft) })}
                title={t("workflowBuilder.deleteWorkflow")}
                onClick={event => {
                    deleteTrigger.current = event.currentTarget;
                    setDeleteError("");
                    setDeleteConflict(false);
                    setDeleteTarget(draft);
                }}><Trash2 size={18} aria-hidden="true" /></Button>
            </div>
            {toggleError && <p className={reminderStyles.libraryError} role="alert">{toggleError}</p>}
        </div>;
    };
    const cards: WorkflowLibraryCard[] = drafts.map(draft => ({
        key: `scheduled:${draft.id}`,
        searchText: [draft.id, draft.title, draft.description, draft.reminder?.message ?? "",
            draft.reminder?.project_summary?.project_name ?? "", draft.reminder?.project_summary?.instructions ?? "",
            draft.reminder?.note_monitor?.project_name ?? "", draft.reminder?.note_monitor?.note_workflow_title ?? ""],
        render: blocked => renderDraftCard(draft, blocked || navigationBlocked)
    }));
    const feedback = <>
            {loading && <div className={renderLibrary ? styles.working : browserStyles.workflowBrowserEmpty}><p role="status">{t("workflowBuilder.loading")}</p></div>}
            {error && <div className={renderLibrary ? styles.error : browserStyles.workflowBrowserEmpty} role="alert">
                <p>{error}</p><Button onClick={() => setAttempt(value => value + 1)}>{t("workflowBuilder.retry")}</Button>
            </div>}
    </>;
    const deleteModal = deleteTarget && <Modal open size="md" className={styles.libraryDeleteDialog} overlayClassName={styles.libraryDeleteOverlay}
            onOpenChange={open => { if (!open) closeDelete(); }}
            onCloseAutoFocus={event => {
                event.preventDefault();
                (deleteTrigger.current?.isConnected ? deleteTrigger.current : list.current)?.focus();
            }}>
            <Modal.Header title={t("workflowBuilder.deleteWorkflowConfirmTitle")}
                description={t("workflowBuilder.deleteWorkflowConfirmDescription", { title: workflowTitle(deleteTarget) })} />
            {deleteError && <Modal.Body><p role="alert" className={styles.error}>{deleteError}</p></Modal.Body>}
            <Modal.Footer className={styles.libraryDeleteActions}>
                <Button autoFocus variant="secondary" disabled={deleting} onClick={closeDelete}>{t("tooltips.cancel")}</Button>
                {deleteConflict ? <Button onClick={() => { closeDelete(); setAttempt(value => value + 1); }}>{t("workflowBuilder.deleteWorkflowReload")}</Button> :
                    <Button variant="danger" isLoading={deleting} disabled={switching || deleting} onClick={() => void confirmDelete()}>{t("workflowBuilder.deleteWorkflow")}</Button>}
            </Modal.Footer>
        </Modal>;
    if (renderLibrary) return <section ref={list} tabIndex={-1}>
        {renderLibrary({ cards, loading, feedback, navigationBlocked, hasError: Boolean(error) })}
        {deleteModal}
    </section>;
    return <section className={browserStyles.workflowBrowserList}>
        <header className={browserStyles.workflowBrowserHeader}>
            <div className={browserStyles.workflowBrowserHeading}>
                <h2>{title ?? t("workflowBuilder.libraryTitle")}</h2>
                <p>{loading ? t("settingsPage.loading") : normalizedSearchQuery
                    ? t("settingsPage.company.noteWorkflows.searchResultCount", { visible: visibleDrafts.length, total: drafts.length })
                    : t("settingsPage.company.noteWorkflows.workflowCount", { count: drafts.length })}</p>
            </div>
            <div className={browserStyles.workflowBrowserControls}>
                <label className={browserStyles.workflowSearch}>
                    <Search size={17} aria-hidden="true" />
                    <span className={browserStyles.editorWindowAccessibleTitle}>{t("settingsPage.company.noteWorkflows.searchLabel")}</span>
                    <input type="search" value={searchQuery} disabled={loading || switching || deleting || drafts.length === 0}
                        placeholder={t("settingsPage.company.noteWorkflows.searchPlaceholder")}
                        onChange={event => {
                            setSearchQuery(event.target.value);
                            setDeleteTarget(null);
                            setDeleteError("");
                            setDeleteConflict(false);
                        }} />
                </label>
                <button type="button" className={browserStyles.workflowCreateButton} onClick={onCreate} disabled={navigationBlocked}>
                    <Plus size={17} aria-hidden="true" />{t("settingsPage.company.noteWorkflows.actions.newWorkflow")}
                </button>
            </div>
        </header>
        <div ref={element => { list.current = element; }} tabIndex={-1}>
            {feedback}
            {!loading && !error && drafts.length === 0 && <div className={browserStyles.workflowBrowserEmpty}>
                <p>{t("workflowBuilder.libraryEmpty")}</p>
                <button type="button" className={browserStyles.workflowCreateButton} onClick={onCreate} disabled={switching || deleting}>
                    <Plus size={17} aria-hidden="true" />{t("settingsPage.company.noteWorkflows.actions.newWorkflow")}
                </button>
            </div>}
            {!loading && !error && drafts.length > 0 && visibleDrafts.length === 0 && <div className={browserStyles.workflowBrowserEmpty}>
                <p>{t("settingsPage.company.noteWorkflows.noSearchResults")}</p>
                <button type="button" className={browserStyles.workflowBrowserClearSearch} onClick={() => setSearchQuery("")}>{t("settingsPage.company.noteWorkflows.clearSearch")}</button>
            </div>}
            {visibleDrafts.length > 0 && <div className={browserStyles.workflowBrowserCards}>{visibleDrafts.map(draft => renderDraftCard(draft))}</div>}
        </div>
        {deleteModal}
    </section>;
}
